import { Button, Icon, TextButton, Trigger, isSuccess, useLaravelCrudEntity, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Address } from "../../../../shared/ui/Address";
import { addressService, companyAddressService } from "../../shared/api/addressService";
import { companyService } from "../../shared/api/companyService";
import { useDefaultCountry } from "../../shared/hooks";
import { CompanyAddressFormState, ICompanyUpdateResource } from "../../shared/types";
import {
	DrawerFormButtonContainer,
	MarkAsPrimaryButton,
	PrimaryLabel,
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionItem,
	ViewPageSectionListItem,
} from "../../shared/view";
import { ValueWrapper } from "../../shared/view/ViewComponents";
import { CompanyAddressDrawer } from "./CompanyAddressDrawer";

const StyledViewPageSection = styled(ViewPageSection)`
	grid-area: address;
`;

const StyledPrimaryLabel = styled(PrimaryLabel)`
	align-self: center;
`;

export const CompanyViewAddressSection: FC<{ company: ICompanyUpdateResource }> = ({ company }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const { defaultCountry } = useDefaultCountry();
	const crudOperations = useLaravelCrudEntity(companyAddressService, {
		entityName: t("Address"),
		messages: {
			create: t("Address was saved."),
			update: t("Address was saved."),
			delete: t("Address was deleted."),
		},
	});

	const emptyAddress: CompanyAddressFormState = useMemo(
		() => ({
			city: "",
			street: "",
			houseNumber: "",
			zip: "",
			additionalInfo: "",
			country: defaultCountry || null,
		}),
		[defaultCountry],
	);

	const onClosed = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: companyService.listKey });
		void queryClient.invalidateQueries({ queryKey: companyService.showKey(company.id) });
	}, [company.id, queryClient]);

	return (
		<StyledViewPageSection>
			<ViewPageSectionHeader>{t("Addresses")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				{company.addresses.map((address) => (
					<Trigger
						key={address.id}
						render={(props) => (
							<CompanyAddressDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Edit company address")}
								initialValues={address}
								onClosed={onClosed}
								onSubmit={({ country, ...values }) => {
									if (!country) throw "Missing validation for field: country";

									return crudOperations.updateEntity(address.id, {
										...values,
										companyId: company.id,
										countryId: country.id,
										type: "work",
									});
								}}
							>
								<DrawerFormButtonContainer>
									{!address.isPrimary && (
										<MarkAsPrimaryButton
											entityId={address.id}
											markAsPrimary={addressService.markAsPrimary}
											onSubmit={props.handleClose}
										/>
									)}
									<Button
										type="button"
										color="danger"
										onClick={async () => {
											const res = await companyService.deleteAddress(company.id, address.id);
											if (isSuccess(res)) {
												showToast({
													body: t("Address was deleted."),
													title: t("Address"),
													type: "success",
												});
												props.handleClose();
											}
										}}
									>
										{t("Delete address")}
									</Button>
								</DrawerFormButtonContainer>
							</CompanyAddressDrawer>
						)}
					>
						{(props) => (
							<ViewPageSectionListItem {...props}>
								<ValueWrapper>
									<Address address={address} />
									{address.isPrimary && <StyledPrimaryLabel />}
								</ValueWrapper>
							</ViewPageSectionListItem>
						)}
					</Trigger>
				))}
				<ViewPageSectionItem>
					<Trigger
						render={(props) => (
							<CompanyAddressDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Add company address")}
								initialValues={emptyAddress}
								onClosed={onClosed}
								onSubmit={({ country, ...values }) => {
									if (!country) throw "Missing validation for field: country";

									return crudOperations.createEntity({
										...values,
										companyId: company.id,
										countryId: country.id,
										type: "work",
									});
								}}
							/>
						)}
					>
						{(props) => (
							<TextButton {...props} color="primary" prefix={<Icon $icon="plus" />}>
								{t("Add address")}
							</TextButton>
						)}
					</Trigger>
				</ViewPageSectionItem>
			</ViewPageSectionBody>
		</StyledViewPageSection>
	);
};
